import React, { useEffect, useMemo, useRef, useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { db } from "../../firebase/config";
import firebase from "../../firebase/compat";

const PeriodSelector = ({ page, docid, currentStart, currentEnd }) => {
  // UI state
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [selectedOption, setSelectedOption] = useState("default");

  // Draft state (for the custom inputs)
  const [draftStartDate, setDraftStartDate] = useState(""); // "YYYY-MM-DD"
  const [draftEndDate, setDraftEndDate] = useState("");     // "YYYY-MM-DD"
  const [rangeError, setRangeError] = useState(/** @type {string|null} */ (null));
  
  // Ref to prevent overwriting user input if props update while typing
  const draftDirtyRef = useRef(false);

  const options = useMemo(() => ({ year: "numeric", month: "numeric", day: "numeric" }), []);
  const collection = useMemo(() => {
    if (page === "dashboard") return "Dashboards";
    if (page === "livereport2") return "LiveReports2";
    return "LiveReports";
  }, [page]);

  // --- HELPERS ---

  // Safely convert Firestore Timestamp OR Date object to JS Date
  const getJsDate = (val) => {
    if (!val) return null;
    // Check if it's a Firestore Timestamp (has toDate method)
    if (typeof val.toDate === "function") return val.toDate();
    // Check if it's already a Date object
    if (val instanceof Date) return val;
    return null;
  };

  const pad2 = (n) => String(n).padStart(2, "0");
  
  // Date Object -> "YYYY-MM-DD"
  const toLocalYmd = (dateObj) => {
    if (!dateObj) return "";
    return `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}-${pad2(dateObj.getDate())}`;
  };

  // "YYYY-MM-DD" -> Date Object (Start of day)
  const parseLocalYmd = (ymd) => {
    const [y, m, d] = (ymd || "").split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const sameDay = (d1, d2) => d1?.getTime() === d2?.getTime();

  // --- LOGIC ---

  // Calculate which preset is currently active based on incoming Props
  useEffect(() => {
    const s = getJsDate(currentStart);
    const e = getJsDate(currentEnd);

    if (!s && !e) {
      setSelectedOption("all");
      return;
    }

    if (s && e) {
      const sDay = startOfDay(s);
      const eDay = endOfDay(e);
      const now = new Date();

      // Current Month
      const cmStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const cmEnd = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      if (sameDay(sDay, cmStart) && sameDay(eDay, cmEnd)) {
        setSelectedOption("current-month");
        return;
      }

      // Last Month
      const lmBase = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmStart = new Date(lmBase.getFullYear(), lmBase.getMonth(), 1, 0, 0, 0, 0);
      const lmEnd = endOfDay(new Date(lmBase.getFullYear(), lmBase.getMonth() + 1, 0));
      if (sameDay(sDay, lmStart) && sameDay(eDay, lmEnd)) {
        setSelectedOption("last-month");
        return;
      }

      // Current Year
      const cyStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      const cyEnd = endOfDay(new Date(now.getFullYear(), 11, 31));
      if (sameDay(sDay, cyStart) && sameDay(eDay, cyEnd)) {
        setSelectedOption("current-year");
        return;
      }

      // Last Year
      const lyStart = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      const lyEnd = endOfDay(new Date(now.getFullYear() - 1, 11, 31));
      if (sameDay(sDay, lyStart) && sameDay(eDay, lyEnd)) {
        setSelectedOption("last-year");
        return;
      }
    }

    // If no presets match, it is custom
    setSelectedOption("custom");
  }, [currentStart, currentEnd]);

  // Sync Draft state when opening picker
  useEffect(() => {
    if (showRangePicker && !draftDirtyRef.current) {
      setDraftStartDate(toLocalYmd(getJsDate(currentStart)));
      setDraftEndDate(toLocalYmd(getJsDate(currentEnd)));
    }
  }, [showRangePicker, currentStart, currentEnd]);

  // --- HANDLERS ---

  const saveDatesToFirestore = async (sDate, eDate) => {
    if (!docid) return;

    // LiveReports2 tracks edit recency on every report change; the other
    // collections (Dashboards, legacy LiveReports) don't carry UpdatedAt.
    const recency = collection === "LiveReports2"
      ? { UpdatedAt: firebase.firestore.FieldValue.serverTimestamp() }
      : {};

    // If nulls passed
    if (!sDate && !eDate) {
      await db.collection(collection).doc(docid).update({ Start: null, End: null, ...recency });
      return;
    }

    await db.collection(collection).doc(docid).update({
      Start: firebase.firestore.Timestamp.fromDate(startOfDay(sDate)),
      End: firebase.firestore.Timestamp.fromDate(endOfDay(eDate)),
      ...recency,
    });
  };

  const savePicker = () => {
    // If both empty, clear
    if (!draftStartDate && !draftEndDate) {
      saveDatesToFirestore(null, null);
      setShowRangePicker(false);
      setRangeError(null);
      draftDirtyRef.current = false;
      return;
    }

    const s = parseLocalYmd(draftStartDate);
    const e = parseLocalYmd(draftEndDate);

    if (s && e) {
      // Refuse an inverted range — it would silently filter out all data.
      if (s > e) {
        setRangeError("De einddatum moet op of na de startdatum liggen.");
        return;
      }
      setRangeError(null);
      saveDatesToFirestore(s, e);
      setShowRangePicker(false);
      draftDirtyRef.current = false;
    }
  };

  const cancelPicker = () => {
    draftDirtyRef.current = false;
    setRangeError(null);
    setShowRangePicker(false);
  };

  const periodOptionsHandler = (e) => {
    const value = e.target.value;
    setSelectedOption(value);

    const now = new Date();

    switch (value) {
      case "all":
        saveDatesToFirestore(null, null);
        break;
      case "current-month":
        saveDatesToFirestore(
          new Date(now.getFullYear(), now.getMonth(), 1),
          new Date(now.getFullYear(), now.getMonth() + 1, 0)
        );
        break;
      case "last-month":
        const lmBase = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        saveDatesToFirestore(
            new Date(lmBase.getFullYear(), lmBase.getMonth(), 1),
            new Date(lmBase.getFullYear(), lmBase.getMonth() + 1, 0)
        );
        break;
      case "current-year":
        saveDatesToFirestore(
          new Date(now.getFullYear(), 0, 1),
          new Date(now.getFullYear(), 11, 31)
        );
        break;
      case "last-year":
        saveDatesToFirestore(
          new Date(now.getFullYear() - 1, 0, 1),
          new Date(now.getFullYear() - 1, 11, 31)
        );
        break;
      case "custom":
        setShowRangePicker(true);
        break;
      default:
        break;
    }
  };

  // Prepare display values
  const displayStart = getJsDate(currentStart);
  const displayEnd = getJsDate(currentEnd);

  // Input values (Draft takes precedence if dirty/open, otherwise data from props)
  const inputStartVal = draftStartDate || toLocalYmd(displayStart);
  const inputEndVal = draftEndDate || toLocalYmd(displayEnd);

  return (
    <div id="period-selector-outer-container">
      <select
        id="period-selector-options-select"
        onChange={periodOptionsHandler}
        value={selectedOption}
      >
        <option value="default" disabled>-- Selecteer een periode --</option>
        <option value="all">Alles</option>
        <option value="current-month">Deze maand</option>
        <option value="last-month">Vorige maand</option>
        <option value="current-year">Dit jaar</option>
        <option value="last-year">Vorig jaar</option>
        <option value="custom">Aangepast</option>
      </select>

      <div className="period-select-container">
        <div className="period-select">
          <div id="selected-range-container">
            <p>{displayStart ? displayStart.toLocaleDateString("nl-NL", options) : "Alles"}</p>
            <p>&nbsp; - &nbsp;</p>
            <p>{displayEnd ? displayEnd.toLocaleDateString("nl-NL", options) : "Alles"}</p>

            <CalendarTodayOutlinedIcon
              id="home-range-picker-icon"
              onClick={() => setShowRangePicker(!showRangePicker)}
            />
          </div>

          <div
            id="range-picker-container"
            style={{ display: showRangePicker ? "block" : "none" }}
          >
            <div>
              <div className="range-picker-input-container">
                <p><b>Start</b></p>
                <input
                  type="date"
                  value={inputStartVal}
                  onChange={(e) => {
                    draftDirtyRef.current = true;
                    setRangeError(null);
                    setDraftStartDate(e.target.value);
                  }}
                />
              </div>

              <div className="range-picker-input-container">
                <p><b>Eind</b></p>
                <input
                  type="date"
                  value={inputEndVal}
                  onChange={(e) => {
                    draftDirtyRef.current = true;
                    setRangeError(null);
                    setDraftEndDate(e.target.value);
                  }}
                />
              </div>
            </div>

            {rangeError && <p className="period-selector-range-error">{rangeError}</p>}

            <div id="periodselector-card-button-container">
              <button onClick={cancelPicker}>Annuleren</button>
              <button onClick={savePicker}>Opslaan</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodSelector;